import {
  InteractionStatus,
  PublicClientApplication,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api } from '../api';
import type { AuthConfigOut } from '../types';
import { acquireToken } from './acquireToken';
import { msalConfiguration, tokenRequest } from './config';
import {
  captureAuthFailures,
  clearAuthFailure,
  lastAuthFailure,
  record,
  type AuthFailure,
} from './diagnostics';
import { ReauthGuard } from './reauth';
import { setRequestAuth } from './requestAuth';

/**
 * What the rest of the UI needs to know about the session. `signOut` is null in mock
 * mode, which is how the chrome decides whether to offer a sign-out control at all —
 * rather than each component re-deriving the auth mode for itself.
 */
export interface AuthSession {
  mode: AuthConfigOut['auth_mode'];
  /** From the token, so identity still shows if /api/me fails. */
  accountLabel: string | null;
  signOut: (() => void) | null;
}

const MOCK_SESSION: AuthSession = { mode: 'mock', accountLabel: null, signOut: null };

const AuthSessionContext = createContext<AuthSession>(MOCK_SESSION);

export function useAuthSession(): AuthSession {
  return useContext(AuthSessionContext);
}

type ConfigState =
  | { status: 'loading' }
  | { status: 'ready'; config: AuthConfigOut }
  | { status: 'failed'; error: Error };

/**
 * Fetches `/api/auth-config`, then branches. Nothing below this renders until the auth
 * mode is known, because "does this deployment require a sign-in" cannot be guessed.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfigState>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    api
      .authConfig()
      .then((config) => {
        if (live) setState({ status: 'ready', config });
      })
      .catch((error: unknown) => {
        if (live) {
          setState({
            status: 'failed',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      });
    return () => {
      live = false;
    };
  }, []);

  if (state.status === 'loading') {
    return <Waiting message="Checking how this deployment signs you in..." />;
  }

  /*
   * A failed auth-config is fatal and explicit, never a fall back to mock. Assuming mock
   * would render the full app with no sign-in and no Authorization header against an API
   * that requires one: every request 401s, but the *page* looks like it is working, and
   * "the app is broken" is a much worse diagnosis than "we could not read the sign-in
   * configuration". It is also the one degradation that could misrepresent an
   * unauthenticated session as an authenticated one.
   */
  if (state.status === 'failed') return <ConfigUnavailable error={state.error} />;

  if (state.config.auth_mode === 'mock') return <MockSession>{children}</MockSession>;

  if (state.config.auth_mode === 'demo')
    return <DemoGate config={state.config}>{children}</DemoGate>;

  return <EntraGate config={state.config}>{children}</EntraGate>;
}

/** Where the picked persona lives across reloads, so a refresh mid-demo does not log out. */
const DEMO_PERSONA_KEY = 'demo-persona';

/**
 * Passwordless persona picker for AUTH_MODE=demo. No MSAL and no tenant: the visitor picks
 * one of the personas the API offered, and we register a RequestAuth whose token() returns
 * that persona key verbatim. The backend resolves it through the real role-derivation, so
 * what each persona sees is the genuine RBAC — this only stands in for the sign-in.
 */
function DemoGate({ config, children }: { config: AuthConfigOut; children: ReactNode }) {
  const [persona, setPersona] = useState<string | null>(() =>
    typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(DEMO_PERSONA_KEY),
  );

  // A stored persona that is no longer offered (personas changed between runs) must not
  // wedge the picker shut — treat it as no selection.
  const selected = config.demo_personas.find((p) => p.key === persona) ?? null;

  // Registered during render, not in an effect: App's mount effect fetches before a child
  // effect here would run, so an effect-based registration would let the first load go out
  // with no persona header and 401 — the same ordering trap MockSession/EntraSession note.
  useMemo(() => {
    if (selected) setRequestAuth(demoRequestAuth(selected.key));
    else setRequestAuth(null);
  }, [selected]);

  useEffect(() => () => setRequestAuth(null), []);

  const choose = (key: string) => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(DEMO_PERSONA_KEY, key);
    setPersona(key);
  };

  const signOut = () => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(DEMO_PERSONA_KEY);
    setRequestAuth(null);
    setPersona(null);
  };

  const session = useMemo<AuthSession>(
    () => ({ mode: 'demo' as const, accountLabel: selected?.label ?? null, signOut }),
    [selected],
  );

  if (!selected) {
    return <DemoPicker personas={config.demo_personas} onPick={choose} />;
  }

  return <AuthSessionContext.Provider value={session}>{children}</AuthSessionContext.Provider>;
}

/** A RequestAuth that always returns the chosen persona key. No renewal, no recovery: the
 * persona never expires, and a 401 means it was not seeded, which re-auth cannot fix. */
function demoRequestAuth(personaKey: string) {
  return {
    token: () => Promise.resolve(personaKey),
    recover: () => Promise.resolve(false),
    accepted: () => {},
  };
}

function DemoPicker({
  personas,
  onPick,
}: {
  personas: AuthConfigOut['demo_personas'];
  onPick: (key: string) => void;
}) {
  return (
    <div className="app">
      <div className="signin">
        <h1>OpenRouter Manager</h1>
        <p className="muted">
          Demo sign-in. Pick a role to explore the product as that persona — each sees
          exactly what its real permissions allow.
        </p>
        <div className="demo-personas">
          {personas.map((p) => (
            <button key={p.key} className="demo-persona" onClick={() => onPick(p.key)}>
              <span className="demo-persona-label">{p.label}</span>
              <span className="demo-persona-desc">{p.description}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Mock mode is the whole point of AUTH_MODE=mock: no MSAL, no sign-in UI, and — because
 * nothing is registered with the request layer — no Authorization header. Every developer
 * and the current dev deploy run this way, offline and with no tenant.
 */
function MockSession({ children }: { children: ReactNode }) {
  // useMemo, not useEffect: children fetch in their own mount effects, which run *before*
  // a parent's, so the registry has to be correct during this render. Clearing rather
  // than leaving whatever a previous entra render registered.
  useMemo(() => setRequestAuth(null), []);
  return <AuthSessionContext.Provider value={MOCK_SESSION}>{children}</AuthSessionContext.Provider>;
}

/**
 * Constructed and initialised outside React, exactly once per client id, and the promise
 * itself is cached.
 *
 * Two real traps, both observed in a browser rather than theorised. Constructing a
 * PublicClientApplication pushes into a window-level registry, so it is a side effect and
 * must not repeat — StrictMode double-invokes useMemo's factory. And MSAL's own
 * `initialized` flag is only set after an await, so StrictMode's two provider effects
 * both got past it and MSAL logged "There is already an instance of MSAL.js in the window
 * with the same client id". Initialising here means the provider's own initialize() call
 * is already a no-op by the time it runs.
 */
const instances = new Map<string, Promise<PublicClientApplication>>();

function publicClientApplication(config: AuthConfigOut): Promise<PublicClientApplication> {
  const key = `${config.tenant_id}|${config.client_id}`;
  const existing = instances.get(key);
  if (existing) return existing;
  const created = (async () => {
    const pca = new PublicClientApplication(msalConfiguration(config));
    await pca.initialize();
    // Subscribed before handleRedirectPromise runs, or the failure this exists to capture
    // is the one we miss. MsalProvider calls that internally and discards its rejection.
    captureAuthFailures(pca);
    try {
      await pca.handleRedirectPromise();
    } catch (error) {
      // MsalProvider will call this again and swallow the error; recording it here is the
      // only place the reason survives.
      record(error instanceof Error ? error : new Error(String(error)), 'handleRedirectPromise');
    }
    return pca;
  })();
  instances.set(key, created);
  return created;
}

function EntraGate({ config, children }: { config: AuthConfigOut; children: ReactNode }) {
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    let live = true;
    void publicClientApplication(config).then((pca) => {
      if (live) setInstance(pca);
    });
    return () => {
      live = false;
    };
  }, [config]);

  if (!instance) return <Waiting message="Preparing sign-in..." />;

  return (
    <MsalProvider instance={instance}>
      <EntraSession config={config}>{children}</EntraSession>
    </MsalProvider>
  );
}

function EntraSession({ config, children }: { config: AuthConfigOut; children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  // getActiveAccount is null until a redirect has been handled or an account picked, so
  // fall back to the first known account: a single-tenant SPA has at most one that matters.
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  const session = useMemo<AuthSession>(
    () => ({
      mode: 'entra' as const,
      accountLabel: account ? (account.name ?? account.username) : null,
      signOut: () => void instance.logoutRedirect(),
    }),
    [instance, account],
  );

  // Registered during render for the same reason as MockSession: App's mount effect fires
  // before this component's would, so an effect-based registration would let the first
  // load go out with no Authorization header and 401.
  useMemo(() => {
    if (account) setRequestAuth(entraRequestAuth(instance, account, config));
    else setRequestAuth(null);
  }, [instance, account, config]);

  // Leaving a stale closure registered would let a unmounted session authenticate
  // requests, which in tests silently carries a token between cases.
  useEffect(() => () => setRequestAuth(null), []);

  // Startup covers initialize() and handleRedirectPromise(); a token request before those
  // finish is refused by MSAL. Any other in-progress interaction means we are mid-redirect.
  if (inProgress !== InteractionStatus.None) {
    return <Waiting message="Signing you in with Microsoft Entra ID..." />;
  }

  if (!account) {
    // A recorded failure means we have already been to Entra and come back without a
    // session. Offering the same button again is the redirect loop; show the reason.
    return (
      <SignInPrompt
        failure={lastAuthFailure()}
        onSignIn={() => {
          clearAuthFailure();
          void instance.loginRedirect(tokenRequest(config)).catch((error: unknown) => {
            record(error instanceof Error ? error : new Error(String(error)), 'loginRedirect');
          });
        }}
      />
    );
  }

  return <AuthSessionContext.Provider value={session}>{children}</AuthSessionContext.Provider>;
}

function entraRequestAuth(
  instance: IPublicClientApplication,
  account: AccountInfo,
  config: AuthConfigOut,
) {
  // One guard per registration, so it tracks this account's session rather than the tab.
  const guard = new ReauthGuard();
  return {
    token: () => acquireToken(instance, account, config),
    recover: async () => {
      if (!guard.mayAttempt()) return false;
      guard.consume();
      await instance.acquireTokenRedirect({ ...tokenRequest(config), account });
      return true;
    },
    accepted: () => guard.reset(),
  };
}

/**
 * The three pre-app waits are worded distinctly on purpose: they are consecutive states
 * with different causes (reading the config, constructing MSAL, MSAL interacting), so
 * identical copy would make a hang at one look like a hang at another.
 */
function Waiting({ message }: { message: string }) {
  return (
    <div className="app">
      <div className="loading" role="status" aria-live="polite">
        <p>{message}</p>
      </div>
    </div>
  );
}

function ConfigUnavailable({ error }: { error: Error }) {
  const unreachable = error instanceof ApiError && error.status === 0;
  return (
    <div className="app">
      <div className="error" role="alert">
        <h2>Cannot determine sign-in configuration</h2>
        <p className="error-detail">{error.message}</p>
        <p className="muted">
          The app asks the API which identity provider to use, so it cannot start until
          that answer arrives. It deliberately does not carry on without one: an app
          rendered with no sign-in would look like it was working while every request was
          rejected.
        </p>
        <p className="muted small">
          {unreachable
            ? 'The API did not respond. In development, check the backend is running on port 8000.'
            : `GET /api/auth-config failed${error instanceof ApiError ? ` with HTTP ${error.status}` : ''}.`}
        </p>
        <button className="refresh" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  );
}

function SignInPrompt({
  onSignIn,
  failure,
}: {
  onSignIn: () => void;
  failure?: AuthFailure | null;
}) {
  return (
    <div className="app">
      <div className="signin">
        <h1>OpenRouter Manager</h1>
        <p className="muted">
          Sign in with your work account. Your Entra group membership decides which teams
          and keys you can see.
        </p>
        {failure ? (
          <div className="error" role="alert">
            <h2>Sign-in did not complete</h2>
            <p className="error-detail">
              {failure.errorCode}
              {failure.errorMessage ? `: ${failure.errorMessage}` : ''}
            </p>
            <p className="muted small">
              Failed during {failure.stage}. An `AADSTS…` code above is Entra&apos;s own
              reason and identifies the misconfiguration; give it to whoever administers
              the app registration.
            </p>
          </div>
        ) : null}
        <button className="refresh" onClick={onSignIn}>
          {failure ? 'Try signing in again' : 'Sign in with Microsoft'}
        </button>
      </div>
    </div>
  );
}
